// frontend/grautomates/src/components/ClientInvoiceDetail.jsx
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import PageLayout from '../../shared/PageLayout';
import Select from '../../shared/Select';
import DatePicker from '../../shared/DatePicker';
import { useNotification } from '../../shared/NotificationModal';
import ConfirmationModal from '../../shared/ConfirmationModal';
import { useCurrentUser } from '../../../contexts/UserContext';
import { Table, THead, TBody, TR, TH, TD } from '../../shared/DataTable';
import Button from '../../shared/Button';
import { TEXT } from '../../shared/Typography';
import Alert from '../../shared/Alert';
import LoadingSpinner from '../../shared/LoadingSpinner';
import { QueueChip } from '../../shared/ApprovalQueue';
import { FIELD_INPUT_COMPACT_CLASS, FIELD_LABEL_CLASS } from '../../shared/FormInput';

/**
 * Use the global axios Authorization header if present.
 * Fallback to localStorage.user.token (your App.js writes this).
 */
function getAuthHeaders() {
  const auth = axios?.defaults?.headers?.common?.Authorization;
  if (auth) return { Authorization: auth };
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const { token } = JSON.parse(raw);
      if (token) return { Authorization: `Bearer ${token}` };
    }
  } catch (_err: unknown) { /* Ignore error */ }
  return {};
    // intentionally empty
}

// Approval level -> the job field that names that approver. A constant, so it
// lives at module scope: declared inside the component it was rebuilt on every
// render, which is what made it show up as a missing useMemo dependency.
const levelKeyMap = { admin: 'jobAdministrator', manager: 'jobManager', senior: 'seniorManager' };

export default function ClientInvoiceDetail({ id, onBack }: any) {
  const _currentUser = useCurrentUser();
  const notification = useNotification();
  const [invoice, setInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [me, setMe] = useState<any>(null);

  // finance admin override
  const [isFinanceAdmin, setIsFinanceAdmin] = useState(false);
  const [adminLevelOverride, setAdminLevelOverride] = useState('admin');

  // amendment functionality
  const [financeAdmins, setFinanceAdmins] = useState<any[]>([]);
  const [assignedTo, setAssignedTo] = useState('');

  // feature additions (keep UI look):
  const [showAttachments, setShowAttachments] = useState(true); // collapsible attachments
  const [files, setFiles] = useState<any[]>([]); // picked files (upload)
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteAttConfirm, setShowDeleteAttConfirm] = useState<any>(null);

  // edit mode (fields editable except approvers/id/status; admins can edit approvers)
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<any>(null);

  // ---- data load ----
  async function fetchMe() {
    try {
      const r = await axios.get('/auth/me');
      const m = r?.data || {};
      setMe(m);
      const roles =
        Array.isArray(m?._computedRoles) && m._computedRoles.length > 0
          ? m._computedRoles
          : (m?.dbUser?.roles || []).map(r => r?.name || r);

      console.info('User Roles for Invoice Edit:', {
        allRoles: roles,
        email: m?.email,
      });

      const hasAdmin = (roles || []).some(s => {
        const role = String(s).toLowerCase();
        return (
          role === 'platform:admin' || role === 'admin' || role.startsWith('financial_invoices:')
        );
      });

      console.info('Is Finance Admin:', hasAdmin);
      setIsFinanceAdmin(!!hasAdmin);
    } catch (_err: unknown) {
      // don't blow up UI if /auth/me fails; interceptor handles auth state globally
    }
  }

  async function fetchInvoice() {
    if (!id) return;
    setLoading(true);
    try {
      const r = await axios.get(`/api/client-invoices/${id}`);
      setInvoice(r.data || null);

      // if status implies a level, preselect it for admin override dropdown
      const nextLevel = statusToLevel((r.data && r.data.status) || '');
      setAdminLevelOverride(nextLevel || 'admin');

      // seed draft for edit mode
      setDraft(makeDraft(r.data || {}));
    } catch (e: any) {
      notification.showError(e?.response?.data?.error || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      fetchMe();
      fetchInvoice();
      fetchFinanceAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-enter edit mode for assigned amendor
  useEffect(() => {
    if (!invoice || !me) return;

    const status = String(invoice?.status || '').toLowerCase();
    const assignedEmail = String(invoice?.assignedTo || '').toLowerCase();
    const myEmail = String(me?.email || '').toLowerCase();

    // If this is a pending amendment AND user is the assigned amendor
    if (status === 'pending amendment' && assignedEmail && myEmail && assignedEmail === myEmail && !editMode) {
      console.info('Auto-entering edit mode for assigned amendor:', { assignedEmail, myEmail });
      startEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, me]);

  async function fetchFinanceAdmins() {
    try {
      const r = await axios.get('/api/client-invoices/finance-admins');
      setFinanceAdmins(Array.isArray(r.data) ? r.data : []);
    } catch (_err: unknown) {
      // Non-critical, just won't show dropdown
    }
  }

  // ----- approvals -----
  const statusToLevel = s => {
    const v = String(s || '').trim();
    if (/^Pending Admin Approval\b/i.test(v)) return 'admin';
    if (/^Pending Manager Approval\b/i.test(v)) return 'manager';
    if (/^Pending Senior Approval\b/i.test(v)) return 'senior';
    return null;
  };

  const currentLevel = useMemo(() => statusToLevel(invoice?.status || ''), [invoice]);

  const split = s =>
    String(s || '')
      .split(/[,\s;]+/)
      .map(x => x.trim())
      .filter(Boolean);

  const approvers = useMemo(() => {
    if (!invoice) return { jobAdministrator: [], jobManager: [], seniorManager: [] };
    const inv = invoice.approvers || {};
    const job = invoice.jobProject || {};
    return {
      jobAdministrator: inv.jobAdministrator?.length
        ? inv.jobAdministrator
        : split(job.jobAdministrator),
      jobManager: inv.jobManager?.length ? inv.jobManager : split(job.jobManager),
      seniorManager: inv.seniorManager?.length ? inv.seniorManager : split(job.seniorManager),
    };
  }, [invoice]);

  const myEmail = (me?.email || '').toLowerCase();
  // Whether this viewer may act now is the server's answer (named approver,
  // their delegate — e.g. for a Position Account — or an admin; never the
  // invoice's creator), so the buttons appear exactly when it will accept them.
  // While pending, that answer decides for admins too (an admin may not approve
  // an invoice they created); otherwise an admin keeps the level override.
  const canAct = currentLevel ? !!invoice?.viewerCanAct : isFinanceAdmin;
  const actsFor = invoice?.viewerActsFor as { email: string; name?: string } | null | undefined;
  const actingLevel = isFinanceAdmin ? adminLevelOverride || currentLevel : currentLevel;

  // Can edit: finance_invoices:admin OR (submitting user AND not fully approved AND next gate not complete)
  const canEdit = useMemo(() => {
    const status = String(invoice?.status || '').toLowerCase();

    // Finance admins can always edit, including "Pending Amendment" invoices
    if (isFinanceAdmin) return true;

    // Assigned amendor or finance admins can edit invoices marked for amendment
    if (status === 'pending amendment') {
      const assignedEmail = String(invoice?.assignedTo || '').toLowerCase();
      console.info('Amendment Edit Check:', {
        status,
        assignedEmail,
        myEmail,
        match: assignedEmail && myEmail && assignedEmail === myEmail,
        invoiceAssignedTo: invoice?.assignedTo,
      });
      if (assignedEmail && myEmail === assignedEmail) {
        return true;
      }
      return isFinanceAdmin;
    }

    // Must be the creator
    if (!myEmail || !invoice?.createdByEmail) return false;
    const isCreator = String(invoice.createdByEmail).toLowerCase() === myEmail;
    if (!isCreator) return false;

    // Cannot edit if fully approved
    if (status === 'approved' || status === 'paid' || status === 'closed') {
      console.info('Edit Permission: Denied - Invoice is fully approved/closed', { status });
      return false;
    }

    // Cannot edit if current approval level has been actioned (approved/rejected by current approver)
    const level = currentLevel; // from statusToLevel
    if (level && invoice?.approvals) {
      const levelKey = levelKeyMap[level];
      const levelApproval = invoice.approvals[levelKey];
      if (levelApproval && levelApproval.status && levelApproval.status !== 'Pending') {
        console.info('Edit Permission: Denied - Current approval gate completed', {
          level,
          levelKey,
          approvalStatus: levelApproval.status,
        });
        return false;
      }
    }

    console.info('Edit Permission: Allowed', {
      isCreator: true,
      status,
      currentLevel: level,
      canEdit: true,
    });
    return true;
  }, [isFinanceAdmin, myEmail, invoice, currentLevel]);

  async function doDeleteInvoice() {
    setShowDeleteConfirm(false);
    try {
      await axios.delete(`/api/client-invoices/${id}`);
      notification.showSuccess('Invoice deleted successfully');
      onBack();
    } catch (e: any) {
      notification.showError(e?.response?.data?.error || e?.message || 'Failed to delete invoice');
    }
  }

  async function handleDecision(status: any) {
    if (!actingLevel) return;
    setLoading(true);
    try {
      await axios.put(
        `/api/client-invoices/${id}/approve`,
        { level: actingLevel, status, comments }
      );
      setComments('');
      // Re-read: the answer carries whether this viewer may act at the next stage too.
      await fetchInvoice();
      notification.showSuccess(`Approval ${status}`);
    } catch (e: any) {
      notification.showError(e?.response?.data?.error || 'Failed to update approval');
    } finally {
      setLoading(false);
    }
  }

  async function handleAmend() {
    if (!assignedTo) {
      notification.showWarning('Please select a user to assign this amendment to.');
      return;
    }
    if (!comments.trim()) {
      notification.showWarning('Please provide amendment notes explaining what needs to be corrected.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `/api/client-invoices/${id}/amend`,
        { comments, assignedTo }
      );
      setComments('');
      setAssignedTo('');
      await fetchInvoice();
      notification.showSuccess('Invoice marked for amendment. The assigned user has been notified.');
    } catch (e: any) {
      notification.showError(e?.response?.data?.error || 'Failed to mark for amendment');
    } finally {
      setLoading(false);
    }
  }

  // ----- attachments -----
  const normAtt = (raw, idx) => {
    if (!raw) return { name: `Attachment ${idx + 1}`, idx };
    if (typeof raw === 'string') {
      return { name: raw, idx };
    }
    if (raw && typeof raw === 'object') {
      return {
        name: raw.originalname || raw.fileName || raw.filename || `Attachment ${idx + 1}`,
        filename: raw.filename,
        idx,
        size: raw.size,
        mimetype: raw.mimetype || raw.contentType,
        // SharePoint fields
        sharePointId: raw.sharePointId,
        webUrl: raw.webUrl,
        editUrl: raw.editUrl,
        downloadUrl: raw.downloadUrl,
      };
    }
    return { name: `Attachment ${idx + 1}`, idx };
  };
  const attachments = useMemo(() => {
    const arr = invoice?.attachments || [];
    return Array.isArray(arr) ? arr.map((a, i) => normAtt(a, i)) : [];
  }, [invoice]);

  async function downloadAttachment(att: any) {
    try {
      const res = await axios.get(`/api/client-invoices/${id}/attachments/${att.idx}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.name || `attachment-${att.idx}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      notification.showError(e?.response?.data?.error || 'Failed to download attachment');
    }
  }

  async function doDeleteAttachment(att: any) {
    setShowDeleteAttConfirm(null);
    try {
      await axios.delete(`/api/client-invoices/${id}/attachments/${att.filename ?? att.idx}`);
      await fetchInvoice();
      notification.showSuccess('Attachment deleted successfully');
    } catch (e: any) {
      notification.showError(e?.response?.data?.error || 'Failed to delete attachment');
    }
  }

  const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.csv', '.xls', '.xlsx', '.doc', '.docx', '.txt', '.msg'];

  function onPickFiles(e: any) {
    const picked: File[] = Array.from(e.target.files || []);
    const rejected: string[] = [];
    const accepted: File[] = [];
    for (const f of picked) {
      const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
      if (ALLOWED_EXTENSIONS.includes(ext)) {
        accepted.push(f);
      } else {
        rejected.push(f.name);
      }
    }
    if (rejected.length > 0) {
      notification.showWarning(
        `Unsupported file type(s) removed: ${rejected.join(', ')}. Allowed: PDF, PNG, JPG, CSV, Excel, Word, TXT, MSG.`
      );
    }
    setFiles(prev => [...prev, ...accepted]);
    e.target.value = '';
  }

  // Upload now; restart approval afterwards (per requirement).
  // When called from saveDraft after a PUT that already handled the
  // amendment notification, pass silentRestart=true so the backend's
  // restart-approval doesn't email creator+finance a second time.
  async function uploadPicked(opts: { silentRestart?: boolean } = {}) {
    if (!files.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach(f => form.append('files', f, f.name));
      const uploadRes = await axios.post(`/api/client-invoices/${id}/attachments`, form, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setFiles([]);
      // restart approval back to first approver when attachments change
      try {
        const url = `/api/client-invoices/${id}/restart-approval${opts.silentRestart ? '?silent=true' : ''}`;
        await axios.post(url);
      } catch (_err: unknown) { /* Ignore error */ }
      await fetchInvoice();
      const warnings = uploadRes.data?._warnings;
      if (warnings && warnings.length > 0) {
        notification.showWarning(`Attachments uploaded with warnings: ${warnings.join('. ')}. Approval returned to first approver.`);
      } else {
        notification.showSuccess('Attachments uploaded. Approval returned to first approver.');
      }
    } catch (e: any) {
      notification.showError(e?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  // ----- edit mode (keep same UI but swap text -> inputs) -----
  function formatDateForInput(dateVal: any) {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  function makeDraft(inv: any) {
    return {
      invoiceNumber: inv?.invoiceNumber || inv?.docRefNo || '',
      clientName: inv?.clientName || '',
      jobProjectName: inv?.jobProject?.name || inv?.jobProject?.title || inv?.jobProjectId || '',
      month: inv?.month || '',
      value: inv?.value ?? '',
      description: inv?.description || '',
      isClientBillable: inv?.isClientBillable !== false,
      // non-editables: approver emails only editable for finance admin (inline in lanes)
      approvers: {
        jobAdministrator: (inv?.approvers?.jobAdministrator || '').toString(),
        jobManager: (inv?.approvers?.jobManager || '').toString(),
        seniorManager: (inv?.approvers?.seniorManager || '').toString(),
      },
      approvalDueDates: {
        jobAdministrator: formatDateForInput(inv?.approvalDueDates?.jobAdministrator),
        jobManager: formatDateForInput(inv?.approvalDueDates?.jobManager),
        seniorManager: formatDateForInput(inv?.approvalDueDates?.seniorManager),
      },
    };
  }
  function startEdit() {
    setDraft(makeDraft(invoice));
    setEditMode(true);
  }
  function cancelEdit() {
    setEditMode(false);
    setDraft(makeDraft(invoice));
  }
  function onDraftChange(field: any, value: any) {
    setDraft(prev => ({ ...prev, [field]: value }));
  }
  function onDraftApproverChange(key: any, value: any) {
    setDraft(prev => ({ ...prev, approvers: { ...(prev?.approvers || {}), [key]: value } }));
  }
  function onDraftDueDateChange(key: any, value: any) {
    setDraft(prev => ({
      ...prev,
      approvalDueDates: { ...(prev?.approvalDueDates || {}), [key]: value },
    }));
  }

  async function saveDraft() {
    if (!draft) return;
    setSaving(true);
    try {
      // payload: all editable fields (NOT id/status)
      const payload: any = {
        invoiceNumber: draft.invoiceNumber,
        clientName: draft.clientName,
        jobProjectName: draft.jobProjectName,
        month: draft.month,
        value: draft.value,
        description: draft.description,
        isClientBillable: draft.isClientBillable,
      };
      if (isFinanceAdmin) {
        payload.approvers = {
          jobAdministrator: split(draft.approvers.jobAdministrator),
          jobManager: split(draft.approvers.jobManager),
          seniorManager: split(draft.approvers.seniorManager),
        };
      }
      // Include approval due dates if editing
      if (canEdit && draft.approvalDueDates) {
        payload.approvalDueDates = {
          jobAdministrator: draft.approvalDueDates.jobAdministrator || null,
          jobManager: draft.approvalDueDates.jobManager || null,
          seniorManager: draft.approvalDueDates.seniorManager || null,
        };
      }

      // Check if this is an amendment before saving (PUT will handle amendment notification)
      const wasAmendment = invoice?.status === 'Pending Amendment';

      await axios.put(`/api/client-invoices/${id}`, payload);

      // if there are files queued, upload them first.
      // When the invoice was amended, the PUT above has already sent
      // the "Action Required" email to the next approver and CC'd
      // creator+finance — pass silentRestart=true so uploadPicked's
      // /restart-approval doesn't fire the creator+finance restart
      // email a second time.
      if (files.length) {
        await uploadPicked({ silentRestart: wasAmendment });
      } else {
        // always restart approval after save per requirement
        // Pass silent=true for amendments since PUT already sends the notification
        try {
          await axios.post(
            `/api/client-invoices/${id}/restart-approval${wasAmendment ? '?silent=true' : ''}`
          );
        } catch (_err: unknown) { /* Ignore error */ }
        await fetchInvoice();
        notification.showSuccess('Saved. Approval returned to first approver.');
      }
      setEditMode(false);
    } catch (e: any) {
      notification.showError(e?.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  // ---- UI ----
  if (!id) {
    return (
      <PageLayout title="Invoice" subtitle="No invoice selected" showLogo={false}>
        <div className={TEXT.bodyMuted}>Choose an invoice from the list.</div>
      </PageLayout>
    );
  }

  return (
    <>
    <PageLayout
      title={`Invoice ${invoice?.invoiceNumber || invoice?.docRefNo || id || ''}`}
      subtitle={invoice?.jobProject?.title ? `Job: ${invoice.jobProject.title}` : 'Client Invoice Details'}
      showLogo={false}
      actions={
        <div className="flex items-center gap-2">
          {!editMode && canEdit && (
            <Button variant="outline" size="xs" onClick={startEdit} disabled={!invoice} title="Edit (will restart approval process when saved)">
              Edit
            </Button>
          )}
          {editMode && (
            <>
              <Button variant="outline" size="xs" onClick={saveDraft} disabled={saving} title="Save changes (will restart approval)">
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" size="xs" onClick={cancelEdit} title="Cancel">
                Cancel
              </Button>
            </>
          )}
          {!editMode && isFinanceAdmin && invoice && (
            <Button variant="dangerOutline" size="xs" onClick={() => setShowDeleteConfirm(true)} title="Delete invoice">
              Delete Invoice
            </Button>
          )}
          <Button variant="outline" size="xs" onClick={onBack}>
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </Button>
        </div>
      }
    >
      {loading && <div className="mb-4"><LoadingSpinner message="Loading…" /></div>}

      {!loading && !invoice && <Alert variant="error" message="Invoice not found." className="mb-6" />}

      {invoice && (
        <>
          {/* Amendment Banner */}
          {String(invoice?.status || '').toLowerCase() === 'pending amendment' &&
           String(invoice?.assignedTo || '').toLowerCase() === myEmail && (
            <Alert
              variant="warning"
              title="Amendment Required"
              message="This invoice has been assigned to you for amendment. Please review and update the required fields below, then save to restart the approval process."
              className="mb-6"
            />
          )}

          {/* Top summary (original layout; swap inputs in edit mode) */}
          <div className="mb-6">
            <h2 className={`mb-4 ${TEXT.sectionTitle}`}>Invoice Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <strong>Invoice #:</strong>{' '}
              {editMode ? (
                <input
                  className={`w-full ${FIELD_INPUT_COMPACT_CLASS}`}
                  value={draft?.invoiceNumber || ''}
                  onChange={e => onDraftChange('invoiceNumber', e.target.value)}
                  // allowed to edit invoice number per your earlier ask; if not, disable this input
                />
              ) : (
                invoice.invoiceNumber || id
              )}
            </div>
            <div>
              <strong>Job Number:</strong>{' '}
              {editMode ? (
                <input
                  className={`w-full ${FIELD_INPUT_COMPACT_CLASS}`}
                  value={draft?.jobProjectName || ''}
                  onChange={e => onDraftChange('jobProjectName', e.target.value)}
                  placeholder="Job code"
                />
              ) : (
                invoice.jobProject?.jobCode ||
                invoice.jobProject?.name ||
                invoice.jobProjectId ||
                '-'
              )}
            </div>
            <div>
              <strong>Job Title:</strong>{' '}
              {invoice.jobProject?.title || invoice.jobProject?.name || '-'}
            </div>
            <div>
              <strong>Month:</strong>{' '}
              {editMode ? (
                <input
                  className={`w-full ${FIELD_INPUT_COMPACT_CLASS}`}
                  value={draft?.month || ''}
                  onChange={e => onDraftChange('month', e.target.value)}
                  placeholder="MM-YYYY or YYYY-MM"
                />
              ) : (
                invoice.month || '-'
              )}
            </div>
            <div>
              <strong>Value:</strong>{' '}
              {editMode ? (
                <input
                  className={`w-full ${FIELD_INPUT_COMPACT_CLASS}`}
                  type="number"
                  step="0.01"
                  value={draft?.value ?? ''}
                  onChange={e => onDraftChange('value', e.target.value)}
                />
              ) : typeof invoice.value === 'number' ? (
                `$${invoice.value}`
              ) : (
                invoice.value || '-'
              )}
            </div>
            <div>
              <strong>Billable Type:</strong>{' '}
              {editMode ? (
                <div className="flex items-center gap-4 mt-1">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="isClientBillable"
                      checked={draft?.isClientBillable === true}
                      onChange={() => onDraftChange('isClientBillable', true)}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className={`ml-2 ${TEXT.body}`}>Client Billable</span>
                  </label>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="isClientBillable"
                      checked={draft?.isClientBillable === false}
                      onChange={() => onDraftChange('isClientBillable', false)}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className={`ml-2 ${TEXT.body}`}>Non-Client Billable</span>
                  </label>
                </div>
              ) : (
                <QueueChip tone={invoice.isClientBillable !== false ? 'blue' : 'neutral'}>
                  {invoice.isClientBillable !== false ? 'Client Billable' : 'Non-Client Billable'}
                </QueueChip>
              )}
            </div>
            <div>
              <strong>Status:</strong>{' '}
              <QueueChip
                tone={
                  invoice.status === 'Approved'
                    ? 'green'
                    : invoice.status === 'Rejected'
                      ? 'red'
                      : 'amber'
                }
              >
                {invoice.status || '—'}
              </QueueChip>
            </div>

            <div className="md:col-span-3">
              <strong>Description:</strong>
              {editMode ? (
                <textarea
                  className={`mt-1 w-full ${FIELD_INPUT_COMPACT_CLASS}`}
                  rows={2}
                  value={draft?.description || ''}
                  onChange={e => onDraftChange('description', e.target.value)}
                />
              ) : (
                <div>{invoice.description || '-'}</div>
              )}
            </div>
            </div>
          </div>

          <hr className="border-gray-100 my-6" />

          {/* Attachments (original table; now collapsible + upload + delete) */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h3 className={`mb-2 ${TEXT.sectionTitle}`}>Attachments</h3>
              <div className="flex items-center gap-2">
                <Button variant="link" size="sm" onClick={() => setShowAttachments(v => !v)}>
                  {showAttachments ? 'Hide' : 'Show'}
                </Button>
                <input
                  id="attPicker"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={onPickFiles}
                  accept=".pdf,.png,.jpg,.jpeg,.csv,.xls,.xlsx,.doc,.docx,.txt,.msg"
                />
                <label
                  htmlFor="attPicker"
                  className="inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  title="Add attachments"
                >
                  Add files
                </label>
                {files.length > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => { void uploadPicked(); }}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading…' : `Upload (${files.length})`}
                  </Button>
                )}
              </div>
            </div>

            {showAttachments &&
              (attachments.length ? (
                <div className="overflow-x-auto">
                  <Table
                    density="compact"
                    scroll={false}
                    className="min-w-full bg-white border border-gray-200 rounded-lg"
                  >
                    <THead>
                      <TR>
                        <TH>File</TH>
                        <TH>Actions</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {attachments.map(a => (
                        <TR key={a.idx}>
                          <TD>
                            <div className="flex flex-col">
                              <span>{a.name}</span>
                              {a.sharePointId && (
                                <span className={`mt-1 ${TEXT.caption}`}>
                                  Stored in SharePoint
                                </span>
                              )}
                            </div>
                          </TD>
                          <TD>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* SharePoint: View button */}
                              {a.webUrl && (
                                <a
                                  href={a.webUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                                  title="View in SharePoint"
                                >
                                  View
                                </a>
                              )}

                              {/* SharePoint: Edit in Office button */}
                              {a.editUrl && (
                                <a
                                  href={a.editUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
                                  title="Edit in Office 365"
                                >
                                  Edit in Office
                                </a>
                              )}

                              {/* Download button (works for both SharePoint and legacy) */}
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => downloadAttachment(a)}
                                title="Download file"
                              >
                                Download
                              </Button>

                              {/* Delete button */}
                              <Button variant="outline" size="xs" onClick={() => setShowDeleteAttConfirm(a)} title="Delete attachment">
                                Delete
                              </Button>
                            </div>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </div>
              ) : (
                <p className={TEXT.bodyMuted}>No attachments.</p>
              ))}
          </div>

          <hr className="border-gray-100 my-6" />

          {/* Approval lanes (original) — admin can edit approver emails inline in edit mode */}
          <div className="mb-6">
            <h3 className={`mb-2 ${TEXT.sectionTitle}`}>
              Approval Flow
              <span className={`ml-3 font-normal ${TEXT.bodyMuted}`}>
                (Sequential: Admin → Manager → Senior)
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {['admin', 'manager', 'senior'].map(lvl => {
                const key = levelKeyMap[lvl];
                const a = invoice.approvals || invoice.approval || {};
                const status =
                  lvl === 'admin'
                    ? a.jobAdministrator?.status || a.jobAdministratorStatus
                    : lvl === 'manager'
                      ? a.jobManager?.status || a.jobManagerStatus
                      : a.seniorManager?.status || a.seniorManagerStatus;

                return (
                  <div
                    key={lvl}
                    className={
                      'p-3 rounded-lg border ' +
                      (currentLevel === lvl ? 'border-blue-400' : 'border-gray-200')
                    }
                  >
                    <div className="font-medium mb-1 capitalize">
                      {lvl}{' '}
                      {currentLevel === lvl ? (
                        <span className={TEXT.linkCaption}>(current)</span>
                      ) : null}
                    </div>
                    <div className="mb-1">
                      <QueueChip
                        tone={
                          status === 'Approved'
                            ? 'green'
                            : status === 'Rejected'
                              ? 'red'
                              : 'neutral'
                        }
                      >
                        {status || 'Pending'}
                      </QueueChip>
                    </div>
                    <div className="text-sm mb-2">
                      {(approvers[key] || []).length ? (
                        !editMode || !isFinanceAdmin ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {approvers[key].map(e => (
                              <li key={e} className="truncate">
                                {e}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <input
                            className={`w-full ${FIELD_INPUT_COMPACT_CLASS}`}
                            value={draft?.approvers?.[key] ?? (approvers[key] || []).join(', ')}
                            onChange={e => onDraftApproverChange(key, e.target.value)}
                            placeholder="emails separated by comma / space"
                          />
                        )
                      ) : !editMode || !isFinanceAdmin ? (
                        <div className="space-y-1">
                          <span className="italic text-gray-500 block">No approver emails</span>
                          {lvl === 'admin' && (
                            <span className={TEXT.linkCaption}>
                              (Optional: Admin approval may be skipped if not required)
                            </span>
                          )}
                        </div>
                      ) : (
                        <input
                          className={`w-full ${FIELD_INPUT_COMPACT_CLASS}`}
                          value={draft?.approvers?.[key] ?? ''}
                          onChange={e => onDraftApproverChange(key, e.target.value)}
                          placeholder="emails separated by comma / space"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <hr className="border-gray-100 my-6" />

          {/* Approval action (with finance admin override) */}
          {canAct && !['Approved', 'Rejected'].includes(invoice.status) && (
            <form onSubmit={e => e.preventDefault()} className="space-y-3 mb-8">
              <div className="p-3 border rounded-lg bg-blue-50 border-blue-200">
                {!isFinanceAdmin && (
                  <Alert
                    variant="info"
                    className="mb-3"
                    message={
                      <>
                        <strong>Sequential Approval:</strong> This invoice is currently at the{' '}
                        <strong className="capitalize">{currentLevel}</strong> approval stage. Once you approve,
                        it will move to the next level in the approval chain.
                        {actsFor && (
                          <>
                            {' '}You are acting for <strong>{actsFor.name || actsFor.email}</strong> as their delegate.
                          </>
                        )}
                      </>
                    }
                  />
                )}
                <div className="flex flex-wrap items-end gap-3 justify-between">
                  <div>
                    <div className="mb-1 font-medium">
                      Your action:
                      {isFinanceAdmin && (
                        <span className={`ml-2 ${TEXT.captionMuted}`}>
                          (acting as {adminLevelOverride})
                        </span>
                      )}
                    </div>
                    {isFinanceAdmin && (
                      <>
                        <div className={`mb-2 ${TEXT.captionMuted}`}>
                          Current stage: <strong className="capitalize">{currentLevel}</strong> |
                          You can approve at any level
                        </div>
                        <Select
                          className="mr-3"
                          value={adminLevelOverride}
                          onChange={setAdminLevelOverride}
                          options={[
                            { value: 'admin', label: 'Admin' },
                            { value: 'manager', label: 'Manager' },
                            { value: 'senior', label: 'Senior' },
                          ]}
                        />
                      </>
                    )}
                    <Button
                      variant="success"
                      size="sm"
                      className="mr-2"
                      onClick={() => handleDecision('Approved')}
                      disabled={loading}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      className="mr-2"
                      onClick={() => handleDecision('Rejected')}
                      disabled={loading}
                    >
                      Reject
                    </Button>
                    {currentLevel === 'admin' && financeAdmins.length > 0 && (
                      <Button variant="warning" size="xs" onClick={handleAmend} disabled={loading}>
                        Amend
                      </Button>
                    )}
                  </div>
                  <div className="flex-1 min-w-[240px] space-y-2">
                    {currentLevel === 'admin' && financeAdmins.length > 0 && (
                      <div>
                        <Select
                          label="Assign Amendment To"
                          value={assignedTo}
                          onChange={setAssignedTo}
                          placeholder="Select a finance admin..."
                          options={financeAdmins.map(admin => ({
                            value: admin.email,
                            label: `${admin.name} (${admin.email})`,
                          }))}
                        />
                      </div>
                    )}
                    <div>
                      <label className={FIELD_LABEL_CLASS}>Comments (optional)</label>
                      <textarea
                        className={`w-full ${FIELD_INPUT_COMPACT_CLASS}`}
                        rows={2}
                        value={comments}
                        onChange={e => setComments(e.target.value)}
                        placeholder="Add a note for the requester"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}

          {canAct && !['Approved', 'Rejected'].includes(invoice.status) && (
            <hr className="border-gray-100 my-6" />
          )}

          {/* Approval due dates + history toggle */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h3 className={TEXT.sectionTitle}>Approval Due Dates</h3>
              <Button variant="link" onClick={() => setShowHistory(v => !v)}>
                {showHistory ? 'Hide History' : 'Show History'}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <div>
                  <strong>Job Administrator:</strong>
                </div>
                {editMode ? (
                  <DatePicker
                    value={draft?.approvalDueDates?.jobAdministrator || ''}
                    onChange={(val) => onDraftDueDateChange('jobAdministrator', val)}
                  />
                ) : (
                  <div>
                    {invoice.approvalDueDates?.jobAdministrator
                      ? new Date(invoice.approvalDueDates.jobAdministrator).toLocaleDateString()
                      : '-'}
                  </div>
                )}
              </div>
              <div>
                <div>
                  <strong>Manager:</strong>
                </div>
                {editMode ? (
                  <DatePicker
                    value={draft?.approvalDueDates?.jobManager || ''}
                    onChange={(val) => onDraftDueDateChange('jobManager', val)}
                  />
                ) : (
                  <div>
                    {invoice.approvalDueDates?.jobManager
                      ? new Date(invoice.approvalDueDates.jobManager).toLocaleDateString()
                      : '-'}
                  </div>
                )}
              </div>
              <div>
                <div>
                  <strong>Senior:</strong>
                </div>
                {editMode ? (
                  <DatePicker
                    value={draft?.approvalDueDates?.seniorManager || ''}
                    onChange={(val) => onDraftDueDateChange('seniorManager', val)}
                  />
                ) : (
                  <div>
                    {invoice.approvalDueDates?.seniorManager
                      ? new Date(invoice.approvalDueDates.seniorManager).toLocaleDateString()
                      : '-'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {showHistory && <hr className="border-gray-100 my-6" />}

          {showHistory && (
            <div className="mb-6">
              <h3 className={`mb-2 ${TEXT.sectionTitle}`}>History</h3>
              {(() => {
                // support both history[] and changeLog[]
                const rows =
                  Array.isArray(invoice.history) && invoice.history.length
                    ? invoice.history
                    : Array.isArray(invoice.changeLog)
                      ? invoice.changeLog.map(c => ({
                          by: c.by || 'system',
                          at: c.at,
                          note:
                            c.note ||
                            (c.stage || c.action
                              ? `${c.stage || ''} ${c.action || ''}`.trim()
                              : ''),
                        }))
                      : [];
                return rows.length ? (
                  <div className="overflow-x-auto">
                    <Table
                      density="compact"
                      scroll={false}
                      className="min-w-full bg-white border border-gray-200 rounded-lg"
                    >
                      <THead>
                        <TR>
                          <TH>By</TH>
                          <TH>At</TH>
                          <TH>Note</TH>
                        </TR>
                      </THead>
                      <TBody>
                        {rows.map((entry, i) => (
                          <TR key={i}>
                            <TD>{entry.by || 'system'}</TD>
                            <TD>
                              {entry.at ? new Date(entry.at).toLocaleString() : '-'}
                            </TD>
                            <TD>{entry.note || ''}</TD>
                          </TR>
                        ))}
                      </TBody>
                    </Table>
                  </div>
                ) : (
                  <p>No history available.</p>
                );
              })()}
            </div>
          )}
        </>
      )}
    </PageLayout>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={doDeleteInvoice}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      <ConfirmationModal
        isOpen={!!showDeleteAttConfirm}
        onClose={() => setShowDeleteAttConfirm(null)}
        onConfirm={() => showDeleteAttConfirm && doDeleteAttachment(showDeleteAttConfirm)}
        title="Delete Attachment"
        message={`Are you sure you want to delete "${showDeleteAttConfirm?.name || showDeleteAttConfirm?.originalname || 'this attachment'}"?`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
}
